/* eslint-disable @typescript-eslint/naming-convention */
import { PropertyType, ResidenceOwnership2 } from '@servicetitan/financing-api-client';
import { z } from 'zod';

const baseSchema = z.object({
    propertyType: z.nativeEnum(PropertyType, { message: 'Select a property type' }),
});

const housingTypeOwn = z
    .object({
        housingType: z.literal(ResidenceOwnership2.Own),
        monthlyHousingPayment: z
            .number({ required_error: 'Enter your monthly mortgage amount' })
            .max(99999, { message: 'Monthly mortgage amount must be less than $100,000' }),
    })
    .merge(baseSchema);
const housingTypeRent = z
    .object({ housingType: z.literal(ResidenceOwnership2.Rent) })
    .merge(baseSchema);

const validateRequired = (
    fieldName: string,
    value: string | undefined,
    message: string,
    ctx: z.RefinementCtx,
) => {
    if (value == null || value.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.invalid_type,
            message,
            expected: z.ZodParsedType.string,
            received: z.ZodParsedType.undefined,
            path: [fieldName],
        });
    }
};

export const schema = z
    .object({
        primaryApplicant: z.discriminatedUnion('housingType', [housingTypeOwn, housingTypeRent], {
            errorMap: () => ({ message: 'Select own or rent' }),
        }),
        improvement: z.string({ required_error: 'Select an improvement type' }),
        workPerformedAtAddress: z.boolean().optional(),
        address: z
            .object({
                street: z.string({ required_error: 'Enter your street address' }).optional(),
                address2: z
                    .string({ required_error: 'Enter your apartment or suite number' })
                    .optional(),
                city: z.string({ required_error: 'Enter your city' }).optional(),
                state: z
                    .string({
                        required_error: 'Select a state',
                        invalid_type_error: 'Select a state',
                    })
                    .optional(),
                zip: z
                    .string({ required_error: 'Enter your zip code' })
                    .regex(/^\d{5}$/, { message: 'Enter a valid zip code' })
                    .optional(),
            })
            .optional(),
    })
    .superRefine((data, ctx) => {
        if (data.workPerformedAtAddress) {
            return;
        }

        validateRequired('address.street', data.address?.street, 'Enter your street address', ctx);
        validateRequired('address.city', data.address?.city, 'Enter your city', ctx);
        validateRequired('address.state', data.address?.state, 'Select a state', ctx);
        validateRequired('address.zip', data.address?.zip, 'Enter your zip code', ctx);
    });

export type ServicePropertyData = z.infer<typeof schema>;
